import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import { LockedUsers, UnlockForm, type LockedUserList } from "./locked-user-list.js";
import { TwoFactorResetSection } from "./two-factor-reset.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/**
 * 今ロックされている人の一覧と、調べた結果（<c>GET /api/admin-center/lockout</c>。サーバの LockoutViewModel と同じ欄）。
 * 一覧はアドレスを指定しなくても毎回届く。
 */
export interface LockoutState extends LockedUserList {
    readonly email: string | null;
    readonly searched: boolean;
    readonly found: boolean;
    readonly isLockedOut: boolean;
    /** ロックの始まりと終わり（日本時間の yyyy/MM/dd HH:mm）。ロック中でなければ null。 */
    readonly lockedSince: string | null;
    readonly lockedUntil: string | null;
    /** 状態の文言。ロック中なら「ロック中（{始まり} から {終わり} まで）」（API が作る）。 */
    readonly statusLabel: string;
    /** 調べた人が認証アプリを登録しているか。登録している人にだけ「二段階認証を解除する」を出す。 */
    readonly twoFactorEnrolled: boolean;
}

function Result({ state }: { readonly state: LockoutState }): ReactElement {
    return (
        <section className="card p-6">
            <h2 className="section-title">{state.email}</h2>

            <p className="mb-4 text-sm">
                状態:{" "}
                {/* ロック中は契約 1 件の利用者の一覧と同じ目立つ赤の札 */}
                <span className={state.isLockedOut ? "badge-danger" : "badge-muted"} data-lock-state="">{state.statusLabel}</span>
            </p>

            {state.found
                ? (
                    <>
                        <p className="mb-4 text-sm text-slate-500">
                            解除すると、連続失敗の回数も 0 に戻ります。
                            戻さないと次の 1 回でまたロックされます。
                        </p>
                        <UnlockForm email={state.email ?? ""} className="btn-primary" />
                        {state.twoFactorEnrolled ? <TwoFactorResetSection email={state.email ?? ""} /> : null}
                    </>
                )
                : (
                    <p className="text-sm text-slate-500">
                        そのアドレスの利用者が見つかりませんでした。綴りをお確かめください。
                    </p>
                )}
        </section>
    );
}

/**
 * ロックの解除。<b>今ロックされている人を、ロックの始まりが新しい順に並べる</b>（上限は API が決める）。
 * 一覧に無い人は、アドレスを指定して調べる。
 */
export function LockoutPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<LockoutState>(adminCenterApi.lockout(search));
    const form = useSearchForm(adminCenterUrls.lockout, {
        Email: data.state === "ready" ? data.value.email ?? "" : "",
    });
    usePageMeta(titleFor(data, "ロックの解除"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="ロックの状態" />;
    }
    const state = data.value;

    return (
        <div className="page max-w-5xl">
            <p className="mb-4"><Link to={adminCenterUrls.menu} className="btn-secondary btn-sm">管理センターへ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">ロックの解除</h1>
                <p className="page-lead">
                    パスワードを続けて間違えると 30 分間ロックされます。待たずに解除できます。
                </p>

                <div className="alert-info mb-4">
                    ロックは<strong>ポータルの画面とプリザンター（<code>/fs</code>）の両方で共有</strong>されます。
                    <code>/fs</code> で間違えた場合もポータルに入れなくなります。
                </div>

                <LockedUsers list={state} />

                <section className="card mb-4 p-6">
                    <h2 className="section-title">メールアドレスで調べる</h2>
                    <form method="get" {...form.props}>
                        <label htmlFor="Email" className="form-label">メールアドレス</label>
                        <div className="flex flex-wrap gap-2">
                            <input
                                id="Email" name="Email" className="form-input grow" autoComplete="off"
                                defaultValue={state.email ?? ""} placeholder="調べたい利用者のメールアドレス"
                            />
                            {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                            <div className="flex gap-2">
                                <button type="submit" className="btn-primary">状態を調べる</button>
                                {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                                <Link to={adminCenterUrls.lockout} className="btn-secondary">クリア</Link>
                            </div>
                        </div>
                        <p className="form-help">一覧に無い人（ロックされていない人など）の状態も、アドレスを指定すれば調べられます。</p>
                    </form>
                </section>

                {state.searched ? <Result state={state} /> : null}
            </div>
        </div>
    );
}
